<template>
  <div class="schedule-grid">
    <div class="actions">
      <span @click="selectDays('all')">{{ $t("platform.all") }} </span>
      <span @click="selectDays('week')"> {{ $t("platform.working_days") }} </span>
      <span @click="selectDays('weekend')"> {{ $t("platform.weekend") }} </span>
      <span @click="selectDays('none')"> {{ $t("platform.turn_off") }}</span>
    </div>

    <div class="header-row">
      <div v-for="hour in hours" :key="hour" class="hour-header">
        <input type="checkbox" v-model="columnChecked[hour]" @change="toggleColumn(hour)" />
        <span class="checkmark">
          <svg viewBox="0 0 24 24">
            <polyline points="5 13 9 17 19 7" />
          </svg>
        </span>
      </div>
    </div>

    <div v-for="(day, dayIndex) in days" :key="day.name" class="day-row">
      <div class="day-label" :class="{ weekend: dayIndex >= 5 }">{{ day.name }}</div>

      <div v-for="hour in hours" :key="hour" class="hour-cell" :class="{ active: isHourSelected(dayIndex, hour) }" @click="toggleHour(dayIndex, hour)">
        {{ hour.toString().padStart(2, "0") }}
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "WorkingSchedule",
  props: {
    initialSchedule: {
      type: Array,
      default: () => [],
    },
    selectAll: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:schedule"],
  data() {
    return {
      isFetching: false,
      dayNames: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      days: [{ name: this.$t("Mon") }, { name: this.$t("Tue") }, { name: this.$t("Wed") }, { name: this.$t("Thu") }, { name: this.$t("Fri") }, { name: this.$t("Sat") }, { name: this.$t("Sun") }],
      hours: Array.from({ length: 24 }, (_, i) => i),
      schedule: Array.from({ length: 7 }, () => Array(24).fill(true)),
      columnChecked: Array(24).fill(true),
    };
  },
  watch: {
    selectAll(val) {
      if (val) this.selectDays("all");
    },
    initialSchedule: {
      handler(newVal) {
        if (newVal && newVal.length) {
          this.setScheduleData(newVal);
        }
      },
      immediate: true,
      deep: true,
    },
    schedule: {
      handler(val) {
        if (this.isFetching) return;
        const payload = val.map((daySchedule, index) => {
          const selectedHours = daySchedule.map((isActive, hourIndex) => (isActive ? hourIndex : null)).filter((i) => i !== null);
          return {
            value: index + 1,
            label: this.dayNames[index],
            [`schedule_day_${index + 1}`]: selectedHours,
            active: selectedHours.length === 24,
          };
        });
        this.schedulePayload = payload;
        this.$emit("update:schedule", payload);
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    selectDays(type) {
      this.schedule.forEach((_, dayIndex) => {
        let isActive = false;

        if (type === "all") {
          isActive = true;
        } else if (type === "week" && dayIndex < 5) {
          isActive = true;
        } else if (type === "weekend" && dayIndex >= 5) {
          isActive = true;
        }

        const newDay = Array(24).fill(isActive);
        this.$set(this.schedule, dayIndex, newDay);
      });

      this.updateColumnChecked();
    },
    toggleHour(dayIndex, hour) {
      this.$set(this.schedule[dayIndex], hour, !this.schedule[dayIndex][hour]);
      this.updateColumnChecked();
    },
    isHourSelected(dayIndex, hour) {
      return this.schedule[dayIndex][hour];
    },
    toggleColumn(hour) {
      const newValue = this.columnChecked[hour];
      this.schedule.forEach((day, dayIndex) => {
        this.$set(this.schedule[dayIndex], hour, newValue);
      });
      this.updateColumnChecked();
    },
    updateColumnChecked() {
      this.hours.forEach((hour) => {
        const isAllOn = this.schedule.every((day) => day[hour]);
        this.$set(this.columnChecked, hour, isAllOn);
      });
    },
    setScheduleData(data) {
      this.isFetching = true;
      this.schedule = Array.from({ length: 7 }, () => Array(24).fill(false));

      data.forEach((item) => {
        const hours = item[`schedule_day_${item.value}`] || [];
        hours.forEach((hour) => {
          if (hour >= 0 && hour < 24) {
            this.schedule[item.value - 1][hour] = true;
          }
        });
      });

      this.updateColumnChecked();
      this.$nextTick(() => {
        this.isFetching = false;
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.schedule-grid {
  display: flex;
  flex-flow: column wrap;
  overflow-x: auto;
  width: 100%;
  .actions {
    display: flex;
    column-gap: 22px;
    margin: 10px 0;
    span {
      cursor: pointer;
      font-size: 14px;
      color: #9b9ba4;
      border-bottom: 1px dashed #9b9ba4;
    }
  }
}

.header-row {
  display: flex;
  padding-left: 30px;
  align-items: center;
}

.hour-header {
  text-align: center;
  display: inline-block;
  position: relative;
  width: 22px;
  height: 22px;
  margin: 1px;
  cursor: pointer;
  input {
    opacity: 0;
    position: absolute;
    width: 100%;
    height: 100%;
    left: 0;
  }
}
.checkmark {
  width: 100%;
  height: 100%;
  border: 1px solid transparent;
  border-radius: 2px;
  background-color: #f4f4f8;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.checkmark svg {
  display: none;
  width: 14px;
  height: 14px;
  stroke: #1b877e;
  stroke-width: 4;
  stroke-linecap: round;
  stroke-linejoin: round;
  fill: none;
}

.hour-header input:checked + .checkmark svg {
  display: block;
}
.hour-header input:checked + .checkmark {
  border: 1px solid #37807d;
}

.day-row {
  display: flex;
  align-items: center;
  margin: 2px 0;
}
.dark .day-label {
  color: #ddd;
}

.day-label {
  width: 30px;
  font-size: 12px;
  font-weight: 500;
}

.day-label.weekend {
  color: brown;
}

.hour-cell {
  width: 22px;
  height: 22px;
  margin: 1px;
  background-color: #f4f4f8;
  color: #7d7d84;
  font-size: 12px;
  text-align: center;
  line-height: 24px;
  cursor: pointer;
  user-select: none;
  position: relative;
}

.hour-cell:hover {
  color: transparent;
}

.hour-cell:hover::after {
  content: "x";
  color: #fff;
  position: absolute;
  background: #7eb4b2;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  text-align: center;
  line-height: 22px;
  pointer-events: none;
}

.hour-cell.active {
  background-color: #37807d;
  color: #ffffff;
  font-weight: bold;
}
</style>
